import { get, post } from '../../networkClient';
import { Path } from '../path';
import { getCommunityFeatureFlagsApi } from './selfServe';

const V2 = true;

export function checkUnitData(communityIds) {
  return post(Path.ONBOARDING_CHECK_UNITS, { communityIds }, null, null, V2);
}

export function checkTourSlots(communityIds) {
  return post(
    Path.ONBOARDING_CHECK_TOUR_SLOTS,
    { communityIds },
    null,
    null,
    V2
  );
}

export function checkLeadSources(communityIds) {
  return post(
    Path.ONBOARDING_CHECK_LEAD_SOURCES,
    { communityIds },
    null,
    null,
    V2
  );
}

export function checkWorkflows(communityIds) {
  return post(
    Path.ONBOARDING_CHECK_WORKFLOWS,
    { communityIds },
    null,
    null,
    V2
  );
}

export function checkAfmSync(leadIds) {
  return post(Path.ONBOARDING_CHECK_AFM_SYNC, { leadIds }, null, null, V2);
}

export function runEndToEndCheck(clientId, communityIds, options = {}) {
  return post(
    Path.ONBOARDING_CHECK_END_TO_END,
    { clientId, communityIds, ...options },
    null,
    null,
    V2
  );
}

export function createTestLeads(clientId, communityIds, options = {}) {
  return post(
    Path.ONBOARDING_CREATE_LEADS,
    { clientId, communityIds, ...options },
    null,
    null,
    V2
  );
}

export function scheduleTours(clientId, tours) {
  return post(
    Path.ONBOARDING_SCHEDULE_TOURS,
    { clientId, tours },
    null,
    null,
    V2
  );
}

export function cleanupOnboarding(leadIds, tourIds) {
  return post(Path.ONBOARDING_CLEANUP, { leadIds, tourIds }, null, null, V2);
}

/**
 * Fetches feature flags for each community and returns a map of
 * communityId -> featureFlags object.
 */
export async function getCommunityFeatureFlags(communityIds) {
  const results = await Promise.all(
    communityIds.map(async id => {
      try {
        const res = await getCommunityFeatureFlagsApi(id);
        return { id, flags: res?.data || {} };
      } catch {
        return { id, flags: {} };
      }
    })
  );
  const flagsByCommunity = {};
  results.forEach(({ id, flags }) => {
    flagsByCommunity[id] = flags;
  });
  return flagsByCommunity;
}

export async function checkHouseAccount(communityIds) {
  const results = await Promise.all(
    communityIds.map(async id => {
      try {
        const res = await get(
          `${Path.COMMUNITY_CONFIG}/${id}/agent`,
          null,
          null,
          true,
          true
        );
        const username = res?.data?.houseAccount?.username;
        return { id, success: !!username, username };
      } catch {
        return { id, success: false };
      }
    })
  );

  const resultsByCommunity = {};
  results.forEach(({ id, success, username }) => {
    resultsByCommunity[id] = {
      success,
      message: success ? username : 'No house account configured'
    };
  });

  const allPassed = results.every(r => r.success);
  const passedCount = results.filter(r => r.success).length;

  return {
    data: {
      status: allPassed ? 'passed' : 'failed',
      message: allPassed
        ? 'All communities have a house account configured'
        : `${passedCount}/${results.length} communities have a house account configured`,
      details: { resultsByCommunity }
    }
  };
}

export async function checkKelseyAgent(communityIds) {
  const results = await Promise.all(
    communityIds.map(async id => {
      try {
        const res = await get(
          `${Path.COMMUNITY_CONFIG}/${id}/agent`,
          null,
          null,
          true,
          true
        );
        const username = res?.data?.kelsey?.username;
        return { id, success: !!username, username };
      } catch {
        return { id, success: false };
      }
    })
  );

  const resultsByCommunity = {};
  results.forEach(({ id, success, username }) => {
    resultsByCommunity[id] = {
      success,
      message: success ? username : 'No Kelsey agent configured'
    };
  });

  const allPassed = results.every(r => r.success);
  const passedCount = results.filter(r => r.success).length;

  return {
    data: {
      status: allPassed ? 'passed' : 'failed',
      message: allPassed
        ? 'All communities have a Kelsey agent configured'
        : `${passedCount}/${results.length} communities have a Kelsey agent configured`,
      details: { resultsByCommunity }
    }
  };
}

/**
 * Checks whether each community's phone number is attached to (a member of)
 * the messaging service sender pool. The endpoint accepts a single
 * `clientId` (the community's tenantId) and `phoneNumber` (the community's
 * phone), so we call it per community and aggregate into the standard
 * readiness-check response shape.
 */
export async function checkMessagingServiceSender(
  clientId,
  communityIds,
  communities
) {
  const phoneByCommunity = {};
  communities.forEach(c => {
    phoneByCommunity[c._id] = c.communityPhone;
  });

  const results = await Promise.all(
    communityIds.map(async id => {
      const communityPhone = phoneByCommunity[id];
      if (!communityPhone) {
        return {
          id,
          success: false,
          message: 'No community phone number configured'
        };
      }
      try {
        const res = await post(
          Path.COMMUNITY_CONFIG_CHECK_MESSAGING_SERVICE_SENDER,
          { clientId, phoneNumber: communityPhone },
          null,
          null,
          V2
        );
        const data = res?.data;
        // The endpoint returns a membership flag; accept the common shapes.
        const isMember =
          data === true ||
          data?.isMember === true ||
          data?.isSender === true ||
          data?.isMessagingServiceSender === true ||
          data?.attached === true;
        return {
          id,
          success: isMember,
          message: isMember
            ? `${communityPhone} is attached to the messaging service`
            : `${communityPhone} is not attached to the messaging service`
        };
      } catch {
        return {
          id,
          success: false,
          message: 'Failed to verify messaging service membership'
        };
      }
    })
  );

  const resultsByCommunity = {};
  results.forEach(({ id, success, message }) => {
    resultsByCommunity[id] = { success, message };
  });

  const allPassed = results.every(r => r.success);
  const passedCount = results.filter(r => r.success).length;

  return {
    data: {
      status: allPassed ? 'passed' : 'failed',
      message: allPassed
        ? 'All community phone numbers are attached to the messaging service'
        : `${passedCount}/${results.length} community phone numbers are attached to the messaging service`,
      details: { resultsByCommunity }
    }
  };
}

export function checkExtraIntegrationSettings(clientId, communityIds) {
  return post(
    Path.ONBOARDING_CHECK_EXTRA_INTEGRATION_SETTINGS,
    { clientId, communityIds },
    null,
    null,
    V2
  );
}

export function checkLeasingAgents(clientId, communityIds) {
  return post(
    Path.ONBOARDING_CHECK_LEASING_AGENTS,
    { clientId, communityIds },
    null,
    null,
    V2
  );
}

export function checkCrmMapping(clientId, communityIds) {
  return post(
    Path.ONBOARDING_CHECK_CRM_MAPPING,
    { clientId, communityIds },
    null,
    null,
    V2
  );
}

export function ingestUnitData(clientId, communityIds) {
  return post(
    Path.ONBOARDING_INGEST_UNITS,
    { clientId, communityIds },
    null,
    null,
    V2
  );
}
